import React from 'react';
import type { PatientRecord } from '../../../patients/types';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

export interface PatientFinancialCardV2Props {
  patient: PatientRecord;
}

/**
 * Same four figures, reordered for the front desk.
 *
 * v1 led with lifetime value — an owner metric that tells the person on the
 * phone nothing they can act on, and one the backend has no field for. It is
 * dropped entirely; outstanding balance leads and is the only row with colour.
 */
const ORDER = ['Outstanding balance', 'Treatment plan', 'Insurance'];
const DROPPED = new Set(['Lifetime value']);

export function PatientFinancialCardV2({ patient: p }: PatientFinancialCardV2Props) {
  const byLabel = new Map(p.financial);
  const rows = ORDER.filter((label) => byLabel.has(label)).map(
    (label) => [label, byLabel.get(label) ?? '—'] as const,
  );
  for (const [label, value] of p.financial) {
    if (!ORDER.includes(label) && !DROPPED.has(label)) rows.push([label, value] as const);
  }

  return (
    <PatientProfileSectionCard icon="dollar" title="Financial" accent="var(--gold)">
      <div className="flex flex-col">
        {rows.map(([label, value], i) => {
          const isBalance = label === 'Outstanding balance';
          const owes = isBalance && value !== '$0' && value !== '—';
          return (
            <div
              key={label}
              className="flex items-center justify-between py-2.5"
              style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--cream-2)' : 'none' }}
            >
              <span
                className="text-[12.5px]"
                style={{ color: 'var(--ink-3)' }}
              >
                {label}
              </span>
              <span
                className="font-semibold"
                style={{
                  color: owes ? 'var(--gold)' : 'var(--ink-0)',
                  fontSize: isBalance ? '15px' : '13.5px',
                  fontFamily: value.includes('$') ? 'var(--font-mono)' : 'var(--font-body)',
                }}
              >
                {value}
              </span>
            </div>
          );
        })}
      </div>
    </PatientProfileSectionCard>
  );
}
